import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import AppShell from "@/components/layout/AppShell";
import api, { fmtError, API_BASE } from "@/lib/api";
import { DataTable, Modal, Btn, Field, TextInput, Textarea, Select, Icons, Badge } from "@/components/ui-lite";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, X, Paperclip, Calculator, Upload, Download, Wallet, RotateCw } from "lucide-react";

function currentMonth() {
  const d = new Date(); d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

const empty = {
  provider_id: "", provider_code: "", month: currentMonth(),
  invoice_number: "", total_amount: 0, currency: "USD",
  exchange_rate_to_usd: null, total_amount_usd: null,
  reported_ggr: null, brand_allocations: [], notes: "", due_date: "",
};

function renderBrandBreakdown(inv, brandsById, brandsByCode) {
  const allocs = inv.brand_allocations || [];
  if (allocs.length === 0) {
    return <div className="text-sm text-[var(--wv-text-3)]">No brand allocations yet. Edit this invoice and click "Auto-allocate by winloss %" to populate.</div>;
  }
  const invFx = Number(inv.exchange_rate_to_usd) || 1;
  return (
    <div>
      <div className="wv-overline mb-2">Brand allocations for {inv.invoice_number || inv.month}</div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-[var(--wv-text-3)] text-xs uppercase tracking-widest">
            <th className="text-left py-1 pr-3">Brand</th>
            <th className="text-right py-1 pr-3">Share %</th>
            <th className="text-right py-1 pr-3">Amount</th>
            <th className="text-right py-1 pr-3">USD</th>
            <th className="text-right py-1 pr-3">Mgmt fee</th>
            <th className="text-right py-1">Total USD</th>
          </tr>
        </thead>
        <tbody>
          {allocs.map((a, idx) => {
            const brand = (a.brand_id && brandsById[a.brand_id]) || (a.brand_code && brandsByCode[a.brand_code]) || null;
            const usd = a.currency === (inv.currency || "USD") ? Number(a.amount) * invFx : Number(a.amount);
            const fee = Number(a.management_fee_usd || 0);
            return (
              <tr key={idx} className="border-t border-white/50">
                <td className="py-1.5 pr-3">
                  <div className="font-medium">{brand?.name || a.brand_code || "—"}</div>
                  <div className="text-xs text-[var(--wv-text-3)] font-mono">{a.brand_code}</div>
                </td>
                <td className="py-1.5 pr-3 text-right font-mono">{a.share_pct != null ? `${Number(a.share_pct).toFixed(1)}%` : "—"}</td>
                <td className="py-1.5 pr-3 text-right font-mono">{Number(a.amount).toFixed(2)} {a.currency}</td>
                <td className="py-1.5 pr-3 text-right font-mono">${usd.toFixed(2)}</td>
                <td className="py-1.5 pr-3 text-right font-mono">{fee ? `$${fee.toFixed(2)}` : "—"}</td>
                <td className="py-1.5 text-right font-mono font-medium">${(usd + fee).toFixed(2)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function InvoicesPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [providers, setProviders] = useState([]);
  const [aggregators, setAggregators] = useState([]);
  const [brands, setBrands] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [month, setMonth] = useState(currentMonth());
  const [scope, setScope] = useState("month"); // "month" | "pending" | "all"
  const [expanded, setExpanded] = useState(null); // invoice id
  const [form, setForm] = useState(empty);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [allocating, setAllocating] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  // ?highlight=<invoice_number> + ?month=YYYY-MM — used when navigating from Monthly Overview
  const highlightNumber = searchParams.get("highlight") || "";

  // If URL has month/highlight, honor them on first load
  useEffect(() => {
    const m = searchParams.get("month");
    if (m && m !== month) { setMonth(m); setScope("month"); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Scroll to + fade the highlighted row once data lands
  useEffect(() => {
    if (!highlightNumber || rows.length === 0) return;
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-highlight-invoice="${highlightNumber}"]`);
      if (el) {
        el.scrollIntoView({ block: "center", behavior: "smooth" });
        el.classList.add("wv-flash-highlight");
        setTimeout(() => el.classList.remove("wv-flash-highlight"), 3000);
      }
      // clear the param so it doesn't re-trigger on subsequent renders
      const sp = new URLSearchParams(searchParams);
      sp.delete("highlight");
      setSearchParams(sp, { replace: true });
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, highlightNumber]);

  const load = () => {
    let url = "/invoices";
    if (scope === "month") url = `/invoices?month=${month}`;
    else if (scope === "pending") url = "/invoices/pending/all";
    return api.get(url).then((r) => setRows(r.data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [month, scope]);
  useEffect(() => {
    api.get("/providers").then((r) => setProviders(r.data));
    api.get("/aggregators").then((r) => setAggregators(r.data));
    api.get("/brands").then((r) => setBrands(r.data));
    api.get("/currencies").then((r) => setCurrencies(r.data));
  }, []);

  const brandsById = useMemo(() => Object.fromEntries(brands.map((b) => [b.id, b])), [brands]);
  const brandsByCode = useMemo(() => Object.fromEntries(brands.map((b) => [b.code, b])), [brands]);
  const providersById = useMemo(() => Object.fromEntries(providers.map((p) => [p.id, p])), [providers]);
  const aggregatorsById = useMemo(() => Object.fromEntries(aggregators.map((a) => [a.id, a])), [aggregators]);

  const save = async () => {
    try {
      const payload = { ...form, total_amount: parseFloat(form.total_amount) };
      if (payload.exchange_rate_to_usd) payload.exchange_rate_to_usd = parseFloat(payload.exchange_rate_to_usd);
      if (payload.reported_ggr) payload.reported_ggr = parseFloat(payload.reported_ggr);
      // Selector value is either "prov:<id>" or "agg:<id>" — decode into provider_code
      const sel = payload.provider_id || "";
      if (sel.startsWith("agg:")) {
        const a = aggregatorsById[sel.slice(4)];
        if (a) { payload.provider_code = a.code; payload.currency = payload.currency || a.invoice_currency || "USD"; }
        payload.provider_id = null;
      } else if (sel.startsWith("prov:")) {
        const p = providersById[sel.slice(5)];
        if (p) { payload.provider_code = p.code; payload.currency = payload.currency || p.invoice_currency || "USD"; }
        payload.provider_id = sel.slice(5);
      } else {
        const p = providersById[sel];
        if (p) { payload.provider_code = p.code; payload.currency = payload.currency || p.invoice_currency || "USD"; }
      }
      let saved;
      if (editing) {
        const { data } = await api.put(`/invoices/${editing.id}`, payload);
        saved = data;
      } else {
        const { data } = await api.post("/invoices", payload);
        saved = data;
      }
      toast.success(editing ? "Saved" : "Invoice created — you can now allocate to brands, upload the file, and record payment below");
      // Keep modal open on create so user can allocate + upload + pay inline
      setEditing(saved);
      setForm({ ...empty, ...saved, provider_id: form.provider_id, exchange_rate_to_usd: saved.exchange_rate_to_usd ?? "", reported_ggr: saved.reported_ggr ?? "" });
      load();
    } catch (e) { toast.error(fmtError(e)); }
  };

  const del = async (id) => { if (window.confirm("Delete invoice?")) { await api.delete(`/invoices/${id}`); load(); } };

  // Inline payment (record + attempt verify against invoice)
  const [payOpen, setPayOpen] = useState(false);
  const [payFor, setPayFor] = useState(null); // invoice being paid
  const [payForm, setPayForm] = useState({ chain: "tron", tx_hash: "", amount: "", currency: "USDT", to_address: "", notes: "" });
  const [paying, setPaying] = useState(false);

  const openPay = (inv) => {
    setPayFor(inv);
    setPayForm({
      chain: "tron",
      tx_hash: "",
      amount: inv.total_amount_usd ?? inv.total_amount ?? "",
      currency: "USDT",
      to_address: "",
      notes: `Invoice ${inv.invoice_number || ""} · ${inv.provider_code} · ${inv.month}`,
    });
    setPayOpen(true);
  };

  const submitPay = async () => {
    if (!payFor) return;
    setPaying(true);
    try {
      const payload = { ...payForm, invoice_id: payFor.id, amount: parseFloat(payForm.amount) || 0 };
      const { data: rec } = await api.post("/payments", payload);
      if (payForm.tx_hash) {
        toast.success("Payment recorded");
        try {
          const { data: ver } = await api.post(`/payments/${rec.id}/verify`);
          toast.success(`On-chain: ${ver.status}`);
        } catch { /* verify failure is non-fatal */ }
      } else {
        toast.success("Payment request logged — add TX hash later to verify");
      }
      setPayOpen(false); setPayFor(null); load();
    } catch (e) { toast.error(fmtError(e)); }
    finally { setPaying(false); }
  };

  const uploadFile = async (file) => {
    if (!file || !editing) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const res = await fetch(`${API_BASE}/invoices/${editing.id}/attachments`, { method: "POST", body: fd, credentials: "include" });
      const j = await res.json();
      if (!res.ok) throw new Error(j.detail || "upload failed");
      toast.success(`Uploaded ${j.filename}`);
      const { data } = await api.get(`/invoices?month=${editing.month}`);
      const updated = data.find((x) => x.id === editing.id);
      setEditing(updated);
      setForm((f) => ({ ...f, attachments: updated?.attachments || [] }));
      load();
    } catch (e) { toast.error(e.message); }
  };

  const removeAttachment = async (filename) => {
    if (!editing) return;
    if (!window.confirm(`Remove ${filename}?`)) return;
    await api.delete(`/invoices/${editing.id}/attachments/${encodeURIComponent(filename)}`);
    const updated = { ...editing, attachments: (editing.attachments || []).filter((a) => a.filename !== filename) };
    setEditing(updated);
    setForm((f) => ({ ...f, attachments: updated.attachments }));
    load();
  };

  const autoAllocate = async () => {
    if (!editing) return;
    setAllocating(true);
    try {
      const { data } = await api.post(`/invoices/${editing.id}/auto-allocate`);
      const updated = { ...editing, brand_allocations: data.allocations };
      setEditing(updated);
      setForm((f) => ({ ...f, brand_allocations: data.allocations }));
      toast.success(`Split ${data.allocations.length} brands · total share $${data.total_provider_share_usd.toLocaleString()}`);
      load();
    } catch (e) { toast.error(fmtError(e)); }
    finally { setAllocating(false); }
  };

  const addAlloc = () => setForm({ ...form, brand_allocations: [...form.brand_allocations, { brand_id: "", brand_code: "", amount: 0, currency: form.currency }] });
  const rmAlloc = (i) => setForm({ ...form, brand_allocations: form.brand_allocations.filter((_, x) => x !== i) });
  const setAlloc = (i, key, val) => {
    const arr = [...form.brand_allocations]; arr[i] = { ...arr[i], [key]: val };
    if (key === "brand_id") { const b = brands.find(x => x.id === val); if (b) arr[i].brand_code = b.code; }
    setForm({ ...form, brand_allocations: arr });
  };

  const statusTone = (s) => s === "verified" ? "success" : s === "paid" ? "info" : s === "carried_forward" ? "warning" : s === "pending" ? "warning" : "default";
  const statusLabel = (s) => s === "carried_forward" ? "carried forward" : (s || "—");

  const toggleCarryForward = async (r) => {
    const isCarried = r.status === "carried_forward";
    if (isCarried) {
      // Uncarry — revert to pending
      if (!window.confirm(`Un-carry invoice ${r.invoice_number}? It will go back to "pending".`)) return;
      try {
        await api.patch(`/invoices/${r.id}/status`, { status: "pending" });
        toast.success("Reverted to pending");
        load();
      } catch (e) { toast.error(fmtError(e)); }
    } else {
      const note = window.prompt(`Mark invoice ${r.invoice_number} as CARRIED FORWARD?\n\nOptional: reason (e.g. "too small this month")`);
      if (note === null) return;
      try {
        await api.patch(`/invoices/${r.id}/status`, { status: "carried_forward", note });
        toast.success("Marked as carried forward — it'll show apart from Pending on Monthly Overview");
        load();
      } catch (e) { toast.error(fmtError(e)); }
    }
  };

  return (
    <AppShell
      title="Invoices"
      subtitle="Vendor invoices due per provider, per brand allocation"
      actions={
        <div className="flex gap-2 items-center">
          <div className="flex rounded-xl bg-white/70 border border-[var(--wv-border)] overflow-hidden text-sm" data-testid="inv-scope">
            <button data-testid="inv-scope-month" onClick={() => setScope("month")} className={`px-3 py-2 ${scope === "month" ? "bg-[var(--wv-primary)] text-white" : "text-[var(--wv-text-2)] hover:bg-white/60"}`}>This month</button>
            <button data-testid="inv-scope-pending" onClick={() => setScope("pending")} className={`px-3 py-2 border-l border-[var(--wv-border)] ${scope === "pending" ? "bg-[var(--wv-primary)] text-white" : "text-[var(--wv-text-2)] hover:bg-white/60"}`}>All pending</button>
            <button data-testid="inv-scope-all" onClick={() => setScope("all")} className={`px-3 py-2 border-l border-[var(--wv-border)] ${scope === "all" ? "bg-[var(--wv-primary)] text-white" : "text-[var(--wv-text-2)] hover:bg-white/60"}`}>All</button>
          </div>
          {scope === "month" && (
            <input data-testid="inv-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-10 px-3 rounded-sm bg-white border border-[var(--wv-border)] font-mono text-sm"/>
          )}
          {isAdmin && <Btn data-testid="inv-add" onClick={() => { setEditing(null); setForm({ ...empty, month }); setOpen(true); }}><Icons.Plus size={16} className="inline mr-1"/> New invoice</Btn>}
        </div>
      }
    >
      <DataTable
        testid="invoices-table"
        columns={[
          { key: "month", label: "Month", mono: true },
          { key: "provider_code", label: "Provider", mono: true },
          { key: "invoice_number", label: "Invoice #", mono: true },
          { key: "total_amount", label: "Amount", mono: true, render: (r) => `${Number(r.total_amount).toFixed(2)} ${r.currency}` },
          { key: "exchange_rate_to_usd", label: "FX", mono: true, render: (r) => r.exchange_rate_to_usd ? Number(r.exchange_rate_to_usd).toFixed(6) : "—" },
          { key: "total_amount_usd", label: "USD", mono: true, render: (r) => r.total_amount_usd ? `$${Number(r.total_amount_usd).toFixed(2)}` : "—" },
          { key: "reported_ggr", label: "Rpt GGR", mono: true, render: (r) => r.reported_ggr ?? "—" },
          { key: "attachments", label: "Docs", render: (r) => r.attachments?.length ? <Badge tone="success"><Paperclip size={12} className="inline mr-1"/>{r.attachments.length}</Badge> : <span className="text-[var(--wv-text-3)]">—</span> },
          { key: "status", label: "Status", render: (r) => (
            <div className="flex flex-col gap-0.5">
              <Badge tone={statusTone(r.status)}>{statusLabel(r.status)}</Badge>
              {r.status === "carried_forward" && r.carry_forward_note && (
                <span className="text-[10px] text-[var(--wv-text-3)] italic max-w-[140px] truncate" title={r.carry_forward_note}>“{r.carry_forward_note}”</span>
              )}
            </div>
          ) },
          { key: "due_date", label: "Due", mono: true },
        ]}
        rows={rows}
        rowProps={(r) => r.invoice_number ? { "data-highlight-invoice": r.invoice_number } : {}}
        expandable={(r) => renderBrandBreakdown(r, brandsById, brandsByCode)}
        expandedRow={expanded}
        onToggleExpand={(id) => setExpanded((cur) => cur === id ? null : id)}
        actions={isAdmin ? (r) => (
          <div className="flex gap-2">
            <button data-testid={`inv-pay-${r.id}`} onClick={() => openPay(r)} title="Pay this invoice" className="text-[var(--wv-text-2)] hover:text-[var(--wv-primary)]"><Wallet size={16}/></button>
            <button
              data-testid={`inv-carry-${r.id}`}
              onClick={() => toggleCarryForward(r)}
              title={r.status === "carried_forward" ? "Un-carry (back to pending)" : "Carry forward to next month"}
              className={r.status === "carried_forward" ? "text-[var(--wv-warning)]" : "text-[var(--wv-text-2)] hover:text-[var(--wv-warning)]"}
            >
              <RotateCw size={16}/>
            </button>
            <button onClick={() => {
              setEditing(r);
              // Compute selector value: prefer aggregator match by code, else provider by code
              let sel = "";
              const codeLower = (r.provider_code || "").toLowerCase();
              const agg = aggregators.find((a) => (a.code || "").toLowerCase() === codeLower);
              if (agg) sel = `agg:${agg.id}`;
              else {
                const p = providers.find((p) => (p.code || "").toLowerCase() === codeLower);
                if (p) sel = `prov:${p.id}`;
              }
              setForm({ ...empty, ...r, provider_id: sel, exchange_rate_to_usd: r.exchange_rate_to_usd ?? "", reported_ggr: r.reported_ggr ?? "" });
              setOpen(true);
            }} className="text-[var(--wv-text-2)] hover:text-[var(--wv-primary)]"><Icons.Pencil size={16}/></button>
            <button onClick={() => del(r.id)} className="text-[var(--wv-text-2)] hover:text-[var(--wv-error)]"><Icons.Trash2 size={16}/></button>
          </div>
        ) : null}
      />

      <Modal open={open} onClose={() => { setOpen(false); setEditing(null); setForm(empty); }} title={editing ? `Edit invoice ${editing.invoice_number || ""}` : "New invoice"}
        footer={<>
          <Btn variant="secondary" onClick={() => { setOpen(false); setEditing(null); setForm(empty); }}>Close</Btn>
          {editing && (
            <Btn variant="secondary" data-testid="inv-pay-from-modal" onClick={() => { openPay(editing); }}>
              <Wallet size={14} className="inline mr-1"/> Record payment
            </Btn>
          )}
          <Btn data-testid="inv-save" onClick={save}>{editing ? "Save changes" : "Create invoice"}</Btn>
        </>}>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Payee (who invoices you)">
            <Select data-testid="inv-provider" value={form.provider_id} onChange={(e) => setForm({ ...form, provider_id: e.target.value })}>
              <option value="">— select —</option>
              {aggregators.length > 0 && (
                <optgroup label="Aggregators">
                  {aggregators.map((a) => <option key={a.id} value={`agg:${a.id}`}>{a.code} — {a.name} ({a.invoice_currency || "USD"})</option>)}
                </optgroup>
              )}
              <optgroup label="Standalone providers (no aggregator)">
                {providers.filter((p) => !p.aggregator_id).map((p) => <option key={p.id} value={`prov:${p.id}`}>{p.code} — {p.name} ({p.invoice_currency || "USD"})</option>)}
              </optgroup>
              <optgroup label="Providers under an aggregator (rarely invoiced directly)">
                {providers.filter((p) => p.aggregator_id).map((p) => <option key={p.id} value={`prov:${p.id}`}>{p.code} — {p.name}</option>)}
              </optgroup>
            </Select>
          </Field>
          <Field label="Month"><TextInput type="month" value={form.month} onChange={(e) => setForm({ ...form, month: e.target.value })} /></Field>
          <Field label="Invoice number"><TextInput data-testid="inv-number" value={form.invoice_number || ""} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} placeholder="Leave blank to auto-generate" className="font-mono"/></Field>
          <Field label="Due date"><TextInput type="date" value={form.due_date || ""} onChange={(e) => setForm({ ...form, due_date: e.target.value })}/></Field>
          <Field label="Amount"><TextInput data-testid="inv-amount" type="number" step="0.01" value={form.total_amount} onChange={(e) => setForm({ ...form, total_amount: e.target.value })} className="font-mono"/></Field>
          <Field label="Currency">
            <select data-testid="inv-currency" value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })} className="w-full h-10 px-3 wv-input text-sm">
              {currencies.length === 0
                ? <option value="USD">USD</option>
                : currencies.map((c) => <option key={c.id} value={c.code}>{c.code} — {c.name}</option>)}
            </select>
          </Field>
          <Field label="FX rate → USD (optional, auto if blank)"><TextInput type="number" step="0.000001" value={form.exchange_rate_to_usd ?? ""} onChange={(e) => setForm({ ...form, exchange_rate_to_usd: e.target.value })} className="font-mono"/></Field>
          <Field label="Reported GGR (by provider)"><TextInput type="number" step="0.01" value={form.reported_ggr ?? ""} onChange={(e) => setForm({ ...form, reported_ggr: e.target.value })} className="font-mono"/></Field>

          <div className="col-span-2">
            <div className="flex items-center justify-between mb-2">
              <div className="wv-overline">Brand allocations</div>
              <div className="flex gap-2">
                {editing && (
                  <button type="button" onClick={autoAllocate} disabled={allocating} className="text-sm text-[var(--wv-primary)] hover:underline flex items-center gap-1 disabled:opacity-60">
                    <Calculator size={14}/> {allocating ? "Computing..." : "Auto-allocate by winloss %"}
                  </button>
                )}
                <button type="button" onClick={addAlloc} className="text-sm text-[var(--wv-primary)] hover:underline flex items-center gap-1"><Plus size={14}/> Add</button>
              </div>
            </div>
            <div className="space-y-2">
              {form.brand_allocations.map((a, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <Select value={a.brand_id || a.brand_code || ""} onChange={(e) => setAlloc(i, "brand_id", e.target.value)}>
                    <option value="">— brand —</option>
                    {brands.map((b) => <option key={b.id} value={b.id}>{b.code} — {b.name}</option>)}
                  </Select>
                  <TextInput type="number" step="0.01" placeholder="Amount" value={a.amount} onChange={(e) => setAlloc(i, "amount", parseFloat(e.target.value))} className="w-40 font-mono"/>
                  <TextInput value={a.currency} onChange={(e) => setAlloc(i, "currency", e.target.value.toUpperCase())} className="w-24 font-mono"/>
                  {a.share_pct != null && <span className="text-xs text-[var(--wv-text-3)] font-mono whitespace-nowrap">{a.share_pct}%</span>}
                  <button onClick={() => rmAlloc(i)} className="text-[var(--wv-text-2)] hover:text-[var(--wv-error)]"><X size={16}/></button>
                </div>
              ))}
            </div>
          </div>

          <div className="col-span-2"><Field label="Notes"><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}/></Field></div>

          {editing && (
            <div className="col-span-2">
              <div className="flex items-center justify-between mb-2">
                <div className="wv-overline">Vendor documents</div>
                <label className="wv-btn-secondary h-9 px-3 rounded-xl text-xs font-medium inline-flex items-center gap-2 cursor-pointer">
                  <Upload size={14}/> Upload file
                  <input data-testid="inv-attach" type="file" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; uploadFile(f); }} />
                </label>
              </div>
              <div className="space-y-2">
                {(editing.attachments || []).map((a) => (
                  <div key={a.filename} className="flex items-center justify-between px-3 py-2 rounded-xl bg-white/70 border border-white/60">
                    <div className="flex items-center gap-2 text-sm min-w-0">
                      <Paperclip size={14} className="text-[var(--wv-text-2)]"/>
                      <span className="font-mono text-xs truncate">{a.filename}</span>
                      <span className="text-xs text-[var(--wv-text-3)]">· {(a.size / 1024).toFixed(1)} KB</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <a href={`${API_BASE}/invoices/${editing.id}/attachments/${encodeURIComponent(a.filename)}`} target="_blank" rel="noreferrer" className="text-[var(--wv-primary)] hover:opacity-80"><Download size={14}/></a>
                      <button onClick={() => removeAttachment(a.filename)} className="text-[var(--wv-text-2)] hover:text-[var(--wv-error)]"><X size={14}/></button>
                    </div>
                  </div>
                ))}
                {(!editing.attachments || editing.attachments.length === 0) && (
                  <div className="text-xs text-[var(--wv-text-3)]">No files uploaded. Upload the vendor's invoice PDF/CSV/image here.</div>
                )}
              </div>
            </div>
          )}
        </div>
      </Modal>

      <Modal open={payOpen} onClose={() => setPayOpen(false)} title={payFor ? `Pay invoice ${payFor.invoice_number || ""}` : "Pay invoice"}
        footer={<>
          <Btn variant="secondary" onClick={() => setPayOpen(false)}>Cancel</Btn>
          <Btn data-testid="inv-pay-save" onClick={submitPay} disabled={paying}>{paying ? "Recording..." : (payForm.tx_hash ? "Record & verify" : "Request payment")}</Btn>
        </>}>
        {payFor && (
          <div className="mb-4 p-3 rounded-xl bg-[var(--wv-accent-light)]/50 text-sm">
            <div className="text-[var(--wv-text-2)]">
              <span className="font-mono">{payFor.provider_code}</span> · {payFor.month} · Invoiced{" "}
              <span className="font-mono font-medium">
                {payFor.total_amount_usd ? `$${Number(payFor.total_amount_usd).toFixed(2)}` : `${payFor.total_amount} ${payFor.currency}`}
              </span>
            </div>
          </div>
        )}
        <div className="grid grid-cols-2 gap-4">
          <Field label="Chain">
            <Select data-testid="inv-pay-chain" value={payForm.chain} onChange={(e) => setPayForm({ ...payForm, chain: e.target.value, currency: e.target.value === "tron" ? "USDT" : "BTC" })}>
              <option value="tron">Tron (USDT TRC20)</option>
              <option value="bitcoin">Bitcoin</option>
            </Select>
          </Field>
          <Field label="Currency">
            <TextInput value={payForm.currency} onChange={(e) => setPayForm({ ...payForm, currency: e.target.value.toUpperCase() })} className="font-mono"/>
          </Field>
          <div className="col-span-2">
            <Field label="TX Hash (leave blank to log request only; paste later to mark paid)">
              <TextInput data-testid="inv-pay-tx" value={payForm.tx_hash} onChange={(e) => setPayForm({ ...payForm, tx_hash: e.target.value.trim() })} className="font-mono" placeholder="0x… / btc txid — optional"/>
            </Field>
          </div>
          <Field label="Amount sent">
            <TextInput data-testid="inv-pay-amount" type="number" step="0.000001" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} className="font-mono"/>
          </Field>
          <Field label="Recipient address (expected)">
            <TextInput data-testid="inv-pay-to" value={payForm.to_address} onChange={(e) => setPayForm({ ...payForm, to_address: e.target.value.trim() })} className="font-mono" placeholder="TXYZ… or bc1…"/>
          </Field>
          <div className="col-span-2">
            <Field label="Notes"><Textarea rows={2} value={payForm.notes} onChange={(e) => setPayForm({ ...payForm, notes: e.target.value })}/></Field>
          </div>
        </div>
      </Modal>
    </AppShell>
  );
}
